import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Input,
  InputNumber,
  Message,
  Spin,
} from "@/frontend/components/common";
import {
  fixtureService,
  teamService,
  tournamentFormatService,
  tournamentGroupService,
  tournamentParticipantService,
  tournamentStandingService,
} from "@/frontend/services";
import type {
  TeamResponseDTO,
  TournamentGroupResponseDTO,
  TournamentStandingResponseDTO,
  FixtureResponseDTO,
} from "@/shared/dto";
import type { StageConfig } from "@/core/value-objects/StageConfig";
import type { TournamentStageInfo } from "../../types";
import { FaBolt, FaGripVertical, FaPlus, FaSyncAlt, FaUsers } from "react-icons/fa";

interface GroupAssignmentBoardProps {
  tournamentId: string;
  seasonId: string;
  formatId: string;
  stage?: StageConfig;
  compact?: boolean;
  tournamentStages: TournamentStageInfo[];
  onManualSetupChange?: (setup: ManualGroupSetup) => void;
}

interface AssignableTeam {
  participantId: string;
  teamId: string;
  name: string;
  code: string;
  flag: string | null;
  groupId: string | null;
}

interface AssignmentColumn {
  id: string | null;
  name: string;
  teams: AssignableTeam[];
  limit?: number;
}

interface ManualGroupSetup {
  type: "GROUPS";
  stageOrder?: number;
  teamsPerGroup: number;
  groups: Array<{ id: string; name: string }>;
  assignments: Record<string, string | null>;
}

function isLocalGroupId(groupId: string | null) {
  return Boolean(groupId?.startsWith("local-group-"));
}

function getFirstGroupFixtures(
  fixtures: FixtureResponseDTO[],
  matchesPerTeam: number
) {
  const playedByParticipant = new Map<string, number>();

  return [...fixtures]
    .sort((a, b) => new Date(a.matchDate).getTime() - new Date(b.matchDate).getTime())
    .filter((fixture) => {
      const homeId = fixture.homeParticipantId;
      const awayId = fixture.awayParticipantId;
      if (!homeId || !awayId) return false;

      const homePlayed = playedByParticipant.get(homeId) ?? 0;
      const awayPlayed = playedByParticipant.get(awayId) ?? 0;
      if (homePlayed >= matchesPerTeam || awayPlayed >= matchesPerTeam) {
        return false;
      }

      playedByParticipant.set(homeId, homePlayed + 1);
      playedByParticipant.set(awayId, awayPlayed + 1);
      return true;
    });
}

export function GroupAssignmentBoard({
  tournamentId,
  seasonId,
  formatId,
  stage,
  compact = false,
  tournamentStages,
  onManualSetupChange,
}: GroupAssignmentBoardProps) {
  const [groups, setGroups] = useState<TournamentGroupResponseDTO[]>([]);
  const [teams, setTeams] = useState<AssignableTeam[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingParticipantId, setSavingParticipantId] = useState<string | null>(null);
  const [draggingParticipantId, setDraggingParticipantId] = useState<string | null>(null);
  const [newGroupName, setNewGroupName] = useState("");
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [autoConfiguring, setAutoConfiguring] = useState(false);
  const [groupSize, setGroupSize] = useState(stage?.structure?.teamsPerGroup ?? 4);

  const tournamentStage = useMemo(() => {
    return tournamentStages.find(
      (item) =>
        item.stageOrder === (stage?.order ?? 1) &&
        ["group", "groups"].includes(item.stageType.toLowerCase())
    );
  }, [stage?.order, tournamentStages]);

  useEffect(() => {
    if (stage?.structure?.teamsPerGroup) {
      setGroupSize(stage.structure.teamsPerGroup);
    }
  }, [stage?.structure?.teamsPerGroup]);

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const [formatRes, groupsRes, participants, standingsRes] = await Promise.all([
        tournamentFormatService.getById(formatId),
        tournamentStage
          ? tournamentGroupService.getByStageId(tournamentStage.id)
          : Promise.resolve({ success: true, data: [] as TournamentGroupResponseDTO[] }),
        tournamentParticipantService.getAll({ tournamentId, seasonId }),
        tournamentStandingService.getBySeasonId(seasonId),
      ]);

      const loadedFormat = formatRes.success && formatRes.data ? formatRes.data : null;
      const setupKey = String(stage?.order ?? 1);
      const manualSetups = loadedFormat?.config?.manualGroupSetups as
        | Record<string, ManualGroupSetup>
        | undefined;
      const manualSetup =
        manualSetups?.[setupKey] ??
        (loadedFormat?.config?.manualGroupSetup as ManualGroupSetup | undefined);
      const nextGroups =
        tournamentStage && groupsRes.success && groupsRes.data
          ? groupsRes.data
          : (manualSetup?.groups ?? []).map((group) => ({
              id: group.id,
              name: group.name,
              stageId: "local",
            }));
      const standings = Array.isArray(standingsRes.data)
        ? (standingsRes.data as TournamentStandingResponseDTO[])
        : [];
      const standingByParticipantId = new Map(
        standings.map((standing) => [standing.participantId, standing])
      );
      const teamIds = Array.from(
        new Set(participants.map((p) => p.teamId).filter((id): id is string => Boolean(id)))
      );
      const teamResponses = await Promise.all(teamIds.map((teamId) => teamService.getById(teamId)));
      const teamById = new Map<string, TeamResponseDTO>();

      teamResponses.forEach((res) => {
        if (res.success && res.data) teamById.set(res.data.id, res.data);
      });

      const nextTeams = participants
        .map((participant) => {
          if (!participant.teamId) return null;
          const team = teamById.get(participant.teamId);
          if (!team) return null;
          return {
            participantId: participant.id,
            teamId: participant.teamId,
            name: team.name,
            code: team.code,
            flag: team.country?.flag ?? null,
            groupId: tournamentStage
              ? standingByParticipantId.get(participant.id)?.groupId ?? null
              : manualSetup?.assignments?.[participant.id] ?? null,
          } satisfies AssignableTeam;
        })
        .filter((team): team is AssignableTeam => team !== null)
        .sort((a, b) => a.name.localeCompare(b.name));

      if (manualSetup?.teamsPerGroup) setGroupSize(manualSetup.teamsPerGroup);
      setGroups(nextGroups);
      setTeams(nextTeams);
    } catch (error) {
      Message.error({
        content: error instanceof Error ? error.message : "No se pudieron cargar los grupos",
      });
    } finally {
      setLoading(false);
    }
  }, [formatId, seasonId, tournamentId, tournamentStage]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const columns = useMemo<AssignmentColumn[]>(() => {
    return groups.map((group) => ({
      id: group.id,
      name: group.name,
      limit: groupSize,
      teams: teams.filter((team) => team.groupId === group.id),
    }));
  }, [groupSize, groups, teams]);

  const availableTeams = useMemo(
    () => teams.filter((team) => !team.groupId),
    [teams]
  );

  useEffect(() => {
    onManualSetupChange?.({
      type: "GROUPS",
      stageOrder: stage?.order ?? 1,
      teamsPerGroup: groupSize,
      groups: groups.map((group) => ({ id: group.id, name: group.name })),
      assignments: Object.fromEntries(
        teams.map((team) => [team.participantId, team.groupId])
      ),
    });
  }, [groupSize, groups, onManualSetupChange, stage?.order, teams]);

  const handleDrop = async (targetGroupId: string | null) => {
    const participantId = draggingParticipantId;
    if (!participantId) return;

    const current = teams.find((team) => team.participantId === participantId);
    if (!current || current.groupId === targetGroupId) {
      setDraggingParticipantId(null);
      return;
    }

    const targetColumn = columns.find((column) => column.id === targetGroupId);
    if (
      targetGroupId &&
      targetColumn?.limit &&
      targetColumn.teams.length >= targetColumn.limit
    ) {
      Message.warning({ content: `${targetColumn.name} ya tiene el cupo completo` });
      setDraggingParticipantId(null);
      return;
    }

    setSavingParticipantId(participantId);
    setTeams((prev) =>
      prev.map((team) =>
        team.participantId === participantId ? { ...team, groupId: targetGroupId } : team
      )
    );

    try {
      if (!isLocalGroupId(targetGroupId)) {
        const res = await tournamentStandingService.assignGroup({
          seasonId,
          participantId,
          groupId: targetGroupId,
        });

        if (!res.success) {
          throw new Error(res.error ?? "No se pudo asignar el equipo al grupo");
        }
      }
    } catch (error) {
      setTeams((prev) =>
        prev.map((team) =>
          team.participantId === participantId ? { ...team, groupId: current.groupId } : team
        )
      );
      Message.error({
        content: error instanceof Error ? error.message : "No se pudo asignar el equipo",
      });
    } finally {
      setSavingParticipantId(null);
      setDraggingParticipantId(null);
    }
  };

  const renderTeamCard = (team: AssignableTeam) => (
    <div
      key={team.participantId}
      draggable={!savingParticipantId}
      onDragStart={() => setDraggingParticipantId(team.participantId)}
      onDragEnd={() => setDraggingParticipantId(null)}
      className={`flex cursor-grab items-center gap-2 rounded border bg-white px-3 py-2 text-sm shadow-sm transition ${
        draggingParticipantId === team.participantId
          ? "border-blue-300 opacity-60"
          : "border-gray-200 hover:border-blue-300"
      } ${savingParticipantId === team.participantId ? "opacity-60" : ""}`}
    >
      <FaGripVertical className="shrink-0 text-gray-300" />
      {team.flag && (
        <img src={team.flag} alt="" className="h-3.5 w-5 rounded object-cover" />
      )}
      <span className="min-w-0 flex-1 truncate font-medium text-gray-700">
        {team.name}
      </span>
      <span className="text-xs text-gray-400">{team.code}</span>
    </div>
  );

  const handleCreateGroup = async () => {
    const trimmedName = newGroupName.trim() || `Grupo ${groups.length + 1}`;
    if (groups.some((group) => group.name.toLowerCase() === trimmedName.toLowerCase())) {
      Message.warning({ content: "Ya existe una cajita con ese nombre" });
      return;
    }

    setCreatingGroup(true);
    try {
      if (!tournamentStage) {
        setGroups((prev) => [
          ...prev,
          {
            id: `local-group-${Date.now()}`,
            stageId: "local",
            name: trimmedName,
          },
        ]);
        setNewGroupName("");
        return;
      }

      const res = await tournamentGroupService.create({
        stageId: tournamentStage.id,
        name: trimmedName,
      });

      if (res.success && res.data) {
        setGroups((prev) => [...prev, res.data!].sort((a, b) => a.name.localeCompare(b.name)));
        setNewGroupName("");
      } else {
        Message.error({ content: res.error ?? "No se pudo crear la cajita" });
      }
    } catch (error) {
      Message.error({
        content: error instanceof Error ? error.message : "No se pudo crear la cajita",
      });
    } finally {
      setCreatingGroup(false);
    }
  };

  const createLocalGroup = useCallback((name: string) => ({
    id: `local-group-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    stageId: "local",
    name,
  }), []);

  const fetchAllSeasonFixtures = useCallback(async () => {
    const allFixtures: FixtureResponseDTO[] = [];
    let cursorDate: string | null | undefined;
    let cursorId: string | null | undefined;

    while (true) {
      const res = await fixtureService.getBySeasonId(seasonId, {
        pageSize: 100,
        direction: "forward",
        cursorDate,
        cursorId,
      });

      if (!res.success || !res.data) break;
      allFixtures.push(...res.data.data);

      if (!res.data.pagination.hasNext || !res.data.pagination.nextCursor) break;
      cursorDate = res.data.pagination.nextCursor.cursorDate;
      cursorId = res.data.pagination.nextCursor.cursorId;
    }

    return allFixtures;
  }, [seasonId]);

  const handleAutoConfigure = async () => {
    setAutoConfiguring(true);
    try {
      const fixtures = await fetchAllSeasonFixtures();
      const candidateFixtures = fixtures.filter(
        (fixture) => fixture.homeParticipantId && fixture.awayParticipantId
      );
      const matchesPerTeam = Math.max(groupSize - 1, 1);
      const sourceFixtures = getFirstGroupFixtures(candidateFixtures, matchesPerTeam);

      if (sourceFixtures.length === 0) {
        Message.warning({ content: "No hay partidos con equipos para autoconfigurar" });
        return;
      }

      const graph = new Map<string, Set<string>>();
      sourceFixtures.forEach((fixture) => {
        const homeId = fixture.homeParticipantId;
        const awayId = fixture.awayParticipantId;
        if (!homeId || !awayId) return;

        if (!graph.has(homeId)) graph.set(homeId, new Set());
        if (!graph.has(awayId)) graph.set(awayId, new Set());
        graph.get(homeId)!.add(awayId);
        graph.get(awayId)!.add(homeId);
      });

      const visited = new Set<string>();
      const components: string[][] = [];

      for (const participantId of graph.keys()) {
        if (visited.has(participantId)) continue;
        const stack = [participantId];
        const component: string[] = [];
        visited.add(participantId);

        while (stack.length > 0) {
          const current = stack.pop()!;
          component.push(current);

          graph.get(current)?.forEach((next) => {
            if (visited.has(next)) return;
            visited.add(next);
            stack.push(next);
          });
        }

        components.push(component);
      }

      components.sort((a, b) => b.length - a.length);
      const nextGroups = components.map((_, index) =>
        createLocalGroup(`Grupo ${String.fromCharCode(65 + index)}`)
      );
      const nextGroupByIndex = new Map(
        nextGroups.map((group, index) => [index, group.id])
      );
      const nextAssignments = new Map<string, string>();

      components.forEach((component, index) => {
        const groupId = nextGroupByIndex.get(index);
        if (!groupId) return;
        component.forEach((participantId) => nextAssignments.set(participantId, groupId));
      });

      const nextGroupSize = Math.max(
        groupSize,
        Math.max(...components.map((item) => item.length), 1)
      );
      const nextTeams = teams.map((team) => ({
          ...team,
          groupId: nextAssignments.get(team.participantId) ?? null,
        }));

      setGroups(nextGroups);
      setGroupSize(nextGroupSize);
      setTeams(nextTeams);
      Message.success({
        content: "Grupos armados con los primeros partidos registrados",
      });
    } catch (error) {
      Message.error({
        content: error instanceof Error ? error.message : "No se pudo autoconfigurar",
      });
    } finally {
      setAutoConfiguring(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-10">
        <Spin size="large" tip="Cargando asignación de grupos..." />
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg">
      <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-3 xl:flex-row xl:items-end xl:justify-between">
        <div className="flex items-center gap-2">
          <FaUsers className="text-blue-500" />
          <div>
            <h4 className="font-semibold text-gray-700">Cajitas de Grupos</h4>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[150px_minmax(160px,1fr)_auto_auto_auto] sm:items-end">
          <InputNumber
            label="Equipos por grupo"
            min={1}
            value={groupSize}
            onChange={(value) => setGroupSize(Number(value) || 1)}
            size="small"
            className="w-full"
          />
          <div>
            <span className="mb-1 block text-xs font-medium text-gray-600">Nombre del grupo</span>
            <Input
              value={newGroupName}
              onChange={(event) => setNewGroupName(event.target.value)}
              onPressEnter={handleCreateGroup}
              placeholder={`Grupo ${groups.length + 1}`}
              size="small"
            />
          </div>
          <Button
            icon={<FaPlus />}
            onClick={handleCreateGroup}
            loading={creatingGroup}
            size="small"
          >
            Agregar grupo
          </Button>
          <Button icon={<FaSyncAlt />} onClick={fetchAssignments} size="small">
            Actualizar
          </Button>
          <Button
            icon={<FaBolt />}
            onClick={handleAutoConfigure}
            loading={autoConfiguring}
            size="small"
          >
            Autoconfigurar
          </Button>
        </div>
      </div>

      {!tournamentStage && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-700">
          Estas cajitas se guardan junto con el formato usando el botón flotante.
        </div>
      )}

      <div className={`grid grid-cols-1 gap-4 p-4 ${compact ? "xl:grid-cols-[1fr_280px]" : "lg:grid-cols-[1fr_320px]"}`}>
        <div className={`grid grid-cols-1 gap-3 md:grid-cols-2 ${compact ? "2xl:grid-cols-3" : "xl:grid-cols-3"}`}>
          {columns.length === 0 ? (
            <div className="flex min-h-48 items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-400 md:col-span-2 xl:col-span-3">
              Agrega un grupo para empezar
            </div>
          ) : (
            columns.map((column) => (
              <div
                key={column.id ?? "unassigned"}
                className="min-h-48 rounded-md border border-gray-200 bg-gray-50"
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => handleDrop(column.id)}
              >
                <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
                  <span className="text-sm font-semibold text-gray-700">{column.name}</span>
                  <span className="text-xs text-gray-500">
                    {column.teams.length}/{column.limit ?? groupSize}
                  </span>
                </div>

                <div className="space-y-2 p-2">
                  {column.teams.length === 0 ? (
                    <div className="flex h-20 items-center justify-center rounded border border-dashed border-gray-300 text-xs text-gray-400">
                      Suelta equipos aquí
                    </div>
                  ) : (
                    column.teams.map(renderTeamCard)
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <div
          className="min-h-48 rounded-md border border-gray-200 bg-slate-50"
          onDragOver={(event) => event.preventDefault()}
          onDrop={() => handleDrop(null)}
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
            <span className="text-sm font-semibold text-gray-700">Equipos disponibles</span>
            <span className="text-xs text-gray-500">{availableTeams.length}</span>
          </div>
          <div className="max-h-96 space-y-2 overflow-y-auto p-2">
            {availableTeams.length === 0 ? (
              <div className="flex h-20 items-center justify-center rounded border border-dashed border-gray-300 text-xs text-gray-400">
                Sin equipos disponibles
              </div>
            ) : (
              availableTeams.map(renderTeamCard)
            )}
            </div>
        </div>
      </div>
    </div>
  );
}
